import React, { useState, useEffect } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomInput, { DATE_MONTH } from './CustomInput';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Header from './Header';
import classnames from 'classnames';
import TimePickerItems, {
  TIME_HOUR,
  TIME_MINUTE,
} from '../Forms/Fields/TimePicker/TimePickerItems/index';
import { constructDateObject } from '../../utils/time/index';
import styles from './index.module.scss';
import toObject from 'dayjs/plugin/toObject';
import utc from 'dayjs/plugin/utc';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { FormHelperText } from '@material-ui/core';
import { TASK_SCHEDULE_LABELS } from 'utils/constants/task';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getUserSelectedLanguage } from 'reducers/users';
import { enUS, id, vi } from 'date-fns/locale';

dayjs.extend(toObject);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const defaultDateFormat = 'dd/MM/yyyy HH:mm';
export const defaultMomentDateFormat = 'DD/MM/YYYY HH:mm';
export const dateOnlyFormat = 'DD/MM/YYYY';
export const defaultDateSeparator = '/';

const useStyles = makeStyles(theme => ({
  disabled: {
    color: `${theme.palette.text.disabled} !important`,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    display: 'flex',
    marginBottom: '0.5rem',
  },
  input: {
    color: theme.palette.text.primary,
  },
  placeholder: {
    fontStyle: 'italic',
    color: theme.palette.text.primary,
  },
  selectMenu: {
    padding: '0.5rem 0',
  },
  compact: {
    margin: 0,
  },
  customPopperColors: {
    '& [class*="react-datepicker"]': {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    '& [class*="customHeader"]': {
      '& Select': {
        color: theme.palette.secondary.main,
      },
      '& [class*="navigation"]': {
        '& [class*="previousMonth"]': {
          color: `${theme.palette.text.primary} !important`,
        },
        '& [class*="nextMonth"]': {
          color: `${theme.palette.text.primary} !important`,
        },
      },
    },
  },
  dateSelectorColors: {
    '& [class*="react-datepicker__day"]': {
      '&:hover': {
        background: theme.palette.primary.light,
      },
    },
    '& [class*="react-datepicker__day-name"]': {
      pointerEvents: 'none',
    },
    '& [class*="react-datepicker__day-names"]': {
      pointerEvents: 'none',
    },
    '& [class*="react-datepicker__day--today"]': {
      background: theme.palette.primary.light,
      '&:hover': {
        background: theme.palette.primary.light,
      },
    },
    '& [class*="react-datepicker__day--selected"]': {
      background: theme.palette.primary.main,
      color: theme.palette.secondary.light,
    },
  },
}));

const DateTimePicker = ({
  label,
  required,
  value,
  error,
  disabled,
  onChange,
  calendarDateFormat = defaultDateFormat,
  systemDateFormat = defaultMomentDateFormat,
  timeInterval = 1,
  showDateTime = true,
  className,
  compact = false,
  minDate,
  endOfDay,
  info,
  displayError = true,
  maxDate,
  onFocus,
  maxMinuteInput,
}) => {
  const { t } = useTranslation();
  const localeMap = {
    en: enUS,
    id: id,
    vn: vi,
  };
  const selectedLanguage = useSelector(state => getUserSelectedLanguage(state));
  registerLocale(selectedLanguage, localeMap[selectedLanguage]);
  const [startDate, setStartDate] = useState(null);
  const [init, setInit] = useState(true);
  const [time, setTime] = useState();
  const [isCalendarOpen, setCalendarOpen] = useState(false);

  const startOrFinishTime = value => {
    if (label === TASK_SCHEDULE_LABELS.finishTime && init) {
      setInit(false);
      return dayjs(value);
    } else {
      return dayjs(value);
    }
  };

  useEffect(() => {
    setStartDate(value ? dayjs(value).toDate() : undefined);
  }, [value]);

  useEffect(() => {
    if (startDate && dayjs(startDate).isValid()) {
      setTime(constructDateObject(startOrFinishTime(startDate).toDate()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const classes = useStyles();

  const onUpdateTime = ({ category, value }) => {
    let newDate = '';
    switch (category) {
      case TIME_HOUR:
        newDate = dayjs(startDate)
          .hour(value)
          .toDate();

        break;
      case TIME_MINUTE:
        newDate = dayjs(startDate)
          .minute(Number(value))
          .toDate();

        break;

      default:
        break;
    }
    onChanges(newDate);
  };

  const [position, setPosition] = useState();

  const CalendarContainer = ({ className, children }) => {
    return (
      <div
        className={classnames(
          styles.customDatePicker,

          className,
        )}
      >
        <div
          className={classnames(
            styles.dateSelector,
            classes.dateSelectorColors,
            {
              [styles.borderRight]: showDateTime,
            },
          )}
        >
          {children}
        </div>
        {showDateTime && (
          <TimePickerItems
            className={styles.timeItemsWrapper}
            timeClassName={styles.timeSelector}
            onUpdateTime={onUpdateTime}
            time={time}
            position={position}
            setPosition={pos => setPosition(pos)}
            timeInterval={timeInterval}
          />
        )}
      </div>
    );
  };

  CalendarContainer.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
  };

  const handleInputChangeUpdate = date => {
    onChanges(date ? dayjs(date).toDate() : null);
  };

  const onChanges = date => {
    if (!date) {
      onChange(undefined);
      setStartDate(undefined);
      setTime(null);
      setInit(true);
      return;
    }

    if (!dayjs(date).isValid()) {
      return;
    }

    const dayjsDate = startOrFinishTime(date);
    const transformDate = endOfDay
      ? dayjsDate.endOf('day').utc()
      : dayjsDate.utc();

    setStartDate(transformDate.toDate());
    const timeObj = transformDate.toObject();

    setTime({ ...timeObj, [DATE_MONTH]: Number(timeObj[DATE_MONTH]) + 1 });
    onChange(transformDate);
  };

  const textErrorOrBool = typeof error === 'string' ? t(error) : error;

  return (
    <FormControl
      className={classnames(
        classes.formControl,
        { [classes.compact]: compact },
        className,
      )}
    >
      <InputLabel
        shrink
        htmlFor="component-error"
        required={required}
        error={Boolean(textErrorOrBool)}
        disabled={disabled}
      >
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <ReactDatePicker
        disabled={disabled}
        fixedHeight
        portalId="datepicker-portal"
        todayButton={t('Today')}
        selected={startDate}
        shouldCloseOnSelect={!showDateTime}
        onChange={onChanges}
        timeFormat="HH:mm"
        dateFormat={calendarDateFormat}
        renderCustomHeader={Header}
        calendarContainer={CalendarContainer}
        popperClassName={classnames(
          classes.customPopperColors,
          styles.customPopper,
        )}
        onCalendarClose={() => setCalendarOpen(false)}
        onCalendarOpen={() => {
          if (typeof onFocus === 'function') {
            onFocus();
          }
          setCalendarOpen(true);
        }}
        locale={selectedLanguage}
        customInputRef="datepicker"
        customInput={
          <CustomInput
            handleCustomChange={handleInputChangeUpdate}
            dateFormat={showDateTime ? systemDateFormat : dateOnlyFormat}
            timeInterval={timeInterval}
            showDateTime={showDateTime}
            compact={compact}
            maxMinuteInput={maxMinuteInput}
            className={classnames({
              [styles.calendarActive]: isCalendarOpen,
              [styles.disabled]: disabled,
            })}
          />
        }
        {...(minDate && { minDate: dayjs(minDate).toDate() })}
        {...(maxDate && { maxDate: dayjs(maxDate).toDate() })}
      />
      {displayError && (
        <FormHelperText id="component-error-text" error={Boolean(error)}>
          {textErrorOrBool}
        </FormHelperText>
      )}
    </FormControl>
  );
};

DateTimePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
  calendarDateFormat: PropTypes.string,
  systemDateFormat: PropTypes.string,
  timeInterval: PropTypes.number,
  maxMinuteInput: PropTypes.number,
  showDateTime: PropTypes.bool,
  className: PropTypes.string,
  compact: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endOfDay: PropTypes.bool,
  info: InfoPropTypes,
  displayError: PropTypes.bool,
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onFocus: PropTypes.func,
};

export default DateTimePicker;
